/* Manual, scroll-snap carousel. No global theme.js edits or autoplay. */
(() => {
  if (customElements.get('inlight-scenes')) return;
  class InlightScenes extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this.rail = this.querySelector('[data-scenes-rail]');
      this.cards = [...this.querySelectorAll('.ilm-scenes__card')];
      this.controls = this.querySelector('[data-scenes-controls]');
      this.dots = this.querySelector('[data-scenes-dots]');
      this.prev = this.querySelector('[data-scenes-prev]');
      this.next = this.querySelector('[data-scenes-next]');
      this.status = this.querySelector('[data-scenes-status]');
      if (!this.rail || !this.cards.length || !this.controls || !this.dots) return;
      this._abort = new AbortController();
      const options = { signal: this._abort.signal };
      this.prev.addEventListener('click', () => this.go(this.page - 1), options);
      this.next.addEventListener('click', () => this.go(this.page + 1), options);
      this.dots.addEventListener('click', event => {
        const dot = event.target.closest('button[data-page]');
        if (dot) this.go(Number(dot.dataset.page));
      }, options);
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail) return;
        const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (direction) { event.preventDefault(); this.go(this.page + direction); }
      }, options);
      this.rail.addEventListener('scroll', () => {
        cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => this.sync());
      }, { ...options, passive: true });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const card = event.target.closest('.ilm-scenes__card');
        if (card) {
          const target = Math.min(this.maximum, Math.max(0, this.cardLeft(card)));
          this.go(this.nearest(target));
        }
      }, options);
      this._resize = new ResizeObserver(() => this.measure());
      this._resize.observe(this.rail);
      this.measure();
    }
    disconnectedCallback() {
      this._abort?.abort();
      this._abort = null;
      this._resize?.disconnect();
      cancelAnimationFrame(this._frame);
    }
    cardLeft(card) {
      return card.getBoundingClientRect().left - this.rail.getBoundingClientRect().left +
        this.rail.scrollLeft - (parseFloat(getComputedStyle(this.rail).paddingLeft) || 0);
    }
    measure() {
      this.maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      this.points = [0];
      this.cards.forEach(card => {
        const target = Math.min(this.maximum, Math.max(0, this.cardLeft(card)));
        if (Math.abs(target - this.points.at(-1)) > 2) this.points.push(target);
      });
      if (this.maximum - this.points.at(-1) > 2) this.points.push(this.maximum);
      const active = this.dots.contains(document.activeElement) ? Number(document.activeElement.dataset.page) : null;
      this.dots.replaceChildren(...this.points.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ilm-scenes__dot';
        button.dataset.page = String(index);
        button.setAttribute('aria-label', `Show page ${index + 1} of ${this.points.length}`);
        button.setAttribute('aria-controls', this.rail.id);
        return button;
      }));
      this.controls.hidden = this.points.length < 2;
      this.sync();
      if (active !== null) this.dots.children[Math.min(active, this.points.length - 1)]?.focus({ preventScroll: true });
    }
    nearest(position) {
      return this.points.reduce((best, point, index) =>
        Math.abs(point - position) < Math.abs(this.points[best] - position) ? index : best, 0);
    }
    sync() {
      this.page = this.nearest(this.rail.scrollLeft);
      [...this.dots.children].forEach((button, index) => {
        if (index === this.page) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.prev.disabled = this.page === 0;
      this.next.disabled = this.page === this.points.length - 1;
    }
    go(index) {
      const page = Math.max(0, Math.min(this.points.length - 1, index));
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollTo({ left: this.points[page], behavior: reduced ? 'auto' : 'smooth' });
      this.status.textContent = `Page ${page + 1} of ${this.points.length}`;
    }
  }
  customElements.define('inlight-scenes', InlightScenes);
})();
